import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Lock, Pencil, Trash2, Unlock, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import {
  type TripDetail,
  closeTrip,
  deleteTrip,
  formatAmount,
  getTrip,
  reopenTrip,
  updateTrip,
} from '@/lib/tickets-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface UserLite {
  id: string;
  fullName: string;
  email: string;
  isActive: boolean;
}

/**
 * Detalle de un viaje para RRHH. Muestra la carpeta de cada empleado
 * (entra para revisar tickets y descargar PDF/Excel) y permite editar,
 * cerrar, reabrir o borrar el viaje completo (en cascada sobre todas las
 * carpetas).
 */
export function AdminTripDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);

  const tripQuery = useQuery({
    queryKey: ['admin', 'trips', 'detail', id],
    queryFn: () => getTrip(id!),
    enabled: !!id,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'trips'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'trips', 'detail', id] });
  };

  const close = useMutation({ mutationFn: () => closeTrip(id!), onSuccess: invalidate });
  const reopen = useMutation({ mutationFn: () => reopenTrip(id!), onSuccess: invalidate });
  const remove = useMutation({
    mutationFn: () => deleteTrip(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'trips'] });
      navigate('/admin/viajes');
    },
  });

  if (!id) return null;
  if (tripQuery.isLoading) return <p className="p-6 text-sm text-muted-foreground">Cargando…</p>;
  if (tripQuery.error || !tripQuery.data) {
    return (
      <p role="alert" className="p-6 text-sm text-destructive">
        {tripQuery.error instanceof ApiError ? tripQuery.error.message : 'Error cargando el viaje'}
      </p>
    );
  }

  const trip = tripQuery.data;
  const isOpen = trip.status === 'OPEN';
  const busy = close.isPending || reopen.isPending || remove.isPending;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <Link
        to="/admin/viajes"
        className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver a viajes
      </Link>

      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden mb-5">
        <header className="px-7 py-5 border-b border-border flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-tightish">{trip.name}</h2>
              <span
                className={cn(
                  'font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
                  isOpen
                    ? 'bg-success/10 border-success/40 text-success'
                    : 'bg-muted border-border text-muted-foreground',
                )}
                style={{ letterSpacing: '0.06em' }}
              >
                {isOpen ? 'En curso' : 'Cerrado'}
              </span>
            </div>
            <p className="text-[13px] text-muted-foreground mt-1">
              {trip.employeeCount} empleado{trip.employeeCount === 1 ? '' : 's'} ·{' '}
              {trip.ticketCount} ticket{trip.ticketCount === 1 ? '' : 's'}
              {trip.totalCents > 0 ? ` · Total ${formatAmount(trip.totalCents)}` : ''}
            </p>
            {trip.tripStartDate ? (
              <p className="text-[12px] text-muted-foreground mt-1">
                {trip.tripStartDate}
                {trip.tripEndDate && trip.tripEndDate !== trip.tripStartDate
                  ? ` → ${trip.tripEndDate}`
                  : ''}
              </p>
            ) : null}
            {trip.description ? (
              <p className="text-[13px] text-muted-foreground mt-1">{trip.description}</p>
            ) : null}
            <p className="text-[12px] text-muted-foreground mt-1">
              Creado por {trip.createdByName}
              {trip.closedAt
                ? ` · Cerrado el ${new Date(trip.closedAt).toLocaleString()} por ${trip.closedByName ?? '?'}`
                : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isOpen ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditing(true)}
                disabled={busy}
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Button>
            ) : null}
            {isOpen ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (
                    confirm(
                      'Cerrar el viaje cerrará la carpeta de todos los empleados y no podrán subir más tickets. ¿Continuar?',
                    )
                  )
                    close.mutate();
                }}
                disabled={busy}
              >
                <Lock className="h-3.5 w-3.5" />
                {close.isPending ? 'Cerrando…' : 'Cerrar viaje'}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (confirm('¿Reabrir el viaje? Los empleados podrán volver a subir tickets.'))
                    reopen.mutate();
                }}
                disabled={busy}
              >
                <Unlock className="h-3.5 w-3.5" />
                {reopen.isPending ? 'Reabriendo…' : 'Reabrir viaje'}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('¿Borrar el viaje entero y las carpetas de todos? Es irreversible.'))
                  remove.mutate();
              }}
              disabled={busy}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Borrar
            </Button>
          </div>
        </header>

        {trip.folders.length === 0 ? (
          <p className="px-7 py-10 text-center text-sm text-muted-foreground">
            El viaje no tiene empleados asignados.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {trip.folders.map((f) => (
              <li key={f.folderId}>
                <Link
                  to={`/admin/tickets/${f.folderId}`}
                  className="flex items-center gap-4 px-7 py-3.5 hover:bg-muted/30"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[14.5px] truncate">{f.ownerName}</p>
                    <p className="text-[12.5px] text-muted-foreground">
                      {f.ticketCount} ticket{f.ticketCount === 1 ? '' : 's'}
                      {f.totalCents > 0 ? ` · ${formatAmount(f.totalCents)}` : ''}
                    </p>
                  </div>
                  <span
                    className={cn(
                      'font-mono text-[10px] font-medium uppercase border rounded-[3px] px-2 py-0.5',
                      f.status === 'OPEN'
                        ? 'bg-success/10 border-success/40 text-success'
                        : 'bg-muted border-border text-muted-foreground',
                    )}
                    style={{ letterSpacing: '0.06em' }}
                  >
                    {f.status === 'OPEN' ? 'En curso' : 'Cerrada'}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {(close.error || reopen.error || remove.error) && (
        <p role="alert" className="text-sm text-destructive">
          {close.error instanceof Error
            ? close.error.message
            : reopen.error instanceof Error
              ? reopen.error.message
              : remove.error instanceof Error
                ? remove.error.message
                : 'Error en la acción'}
        </p>
      )}

      {editing ? (
        <EditTripModal
          trip={trip}
          onClose={() => setEditing(false)}
          onSaved={() => {
            invalidate();
            setEditing(false);
          }}
        />
      ) : null}
    </div>
  );
}

function EditTripModal({
  trip,
  onClose,
  onSaved,
}: {
  trip: TripDetail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(trip.name);
  const [description, setDescription] = useState(trip.description ?? '');
  const [start, setStart] = useState(trip.tripStartDate ?? '');
  const [end, setEnd] = useState(trip.tripEndDate ?? '');
  const [selected, setSelected] = useState<Set<string>>(
    new Set(trip.folders.map((f) => f.ownerUserId)),
  );

  // Empleados que ya subieron tickets: no se pueden quitar (su checkbox
  // queda marcado y deshabilitado). El backend lo valida igualmente.
  const lockedIds = new Set(
    trip.folders.filter((f) => f.ticketCount > 0).map((f) => f.ownerUserId),
  );

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<UserLite[]>('/admin/users'),
  });
  // Empleados activos + los que ya estén en el viaje aunque estén inactivos
  // (para no perderlos de la lista al editar).
  const currentIds = new Set(trip.folders.map((f) => f.ownerUserId));
  const employees = (usersQuery.data ?? []).filter(
    (u) => u.isActive || currentIds.has(u.id),
  );

  const save = useMutation({
    mutationFn: () =>
      updateTrip(trip.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        tripStartDate: start || undefined,
        tripEndDate: end || undefined,
        employeeUserIds: [...selected],
      }),
    onSuccess: () => onSaved(),
  });

  const toggle = (uid: string) => {
    if (lockedIds.has(uid)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  const canSubmit = name.trim().length > 0 && selected.size > 0 && !save.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-sm shadow-xl max-h-[95dvh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <h2 className="text-base font-semibold">Editar viaje</h2>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <form
          className="p-5 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) save.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="edit-trip-name">Nombre del viaje</Label>
            <Input
              id="edit-trip-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-trip-start">Inicio</Label>
              <Input
                id="edit-trip-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-trip-end">Fin</Label>
              <Input
                id="edit-trip-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-trip-desc">Notas (opcional)</Label>
            <Textarea
              id="edit-trip-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Empleados que van ({selected.size} seleccionados)</Label>
            <p className="text-[12px] text-muted-foreground">
              A quien ya haya subido tickets no se le puede quitar (aparece
              bloqueado). Borra primero sus tickets si necesitas sacarlo.
            </p>
            <div className="border border-border rounded-sm max-h-56 overflow-y-auto divide-y divide-border">
              {usersQuery.isLoading ? (
                <p className="px-3 py-3 text-[13px] text-muted-foreground">Cargando empleados…</p>
              ) : employees.length === 0 ? (
                <p className="px-3 py-3 text-[13px] text-muted-foreground">
                  No hay empleados activos.
                </p>
              ) : (
                employees.map((u) => {
                  const locked = lockedIds.has(u.id);
                  return (
                    <label
                      key={u.id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2',
                        locked
                          ? 'opacity-60 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-muted/30',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        disabled={locked}
                        onChange={() => toggle(u.id)}
                        className="h-4 w-4 accent-icaria-600"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="text-[14px] font-medium block truncate">
                          {u.fullName}
                          {locked ? ' · con tickets' : ''}
                        </span>
                        <span className="text-[12px] text-muted-foreground block truncate">
                          {u.email}
                        </span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {save.error ? (
            <p role="alert" className="text-sm text-destructive">
              {save.error instanceof Error ? save.error.message : 'Error guardando los cambios'}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={!canSubmit}>
              {save.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
